


















































































































import Vue from 'vue'
import { ShredderInputData, RatingResult, getRating } from '../DataStructure'

class MotorData {
  rpm: number | null = null

  hz: number | null = null
  poles: number | null = null

  kilowatts: number | null = null
  watts: number | null = null

  volts: number | null = null
  amps: number | null = null
}

class GearingData {
  ratio?: number
  maxTorque?: number
  efficiency: number = 0.75
}

interface JSONDataInput {
  rpm?: number
  kilowatts?: number
  gearRatio?: number
  maxTorque?: number
}

class Hint {
  title: string
  body: string
  emoji: string

  constructor(title: string, body: string, emoji: string) {
    this.title = title
    this.body = body
    this.emoji = emoji
  }
}

export default Vue.extend({
  name: 'ShredderCalculator',
  props: {
    inputData: Object
  },
  created: function () {
    let params = new URLSearchParams(window.location.search);
      let jsonInput = params.get("jsonInput")
      if (jsonInput) {
        let parsedJSON = JSON.parse(jsonInput) as JSONDataInput;
        console.log(parsedJSON);
        if(parsedJSON.rpm) {
          this.motor.rpm = parsedJSON.rpm
        }
        if(parsedJSON.kilowatts) {
          this.motor.kilowatts = parsedJSON.kilowatts
        }
        if(parsedJSON.gearRatio) {
          this.gearing.ratio = parsedJSON.gearRatio
        }
        if(parsedJSON.maxTorque) {
          this.gearing.maxTorque = parsedJSON.maxTorque
        }
      }
    console.log("Calculator created", this.shredderInputData)
  },
  data: function () { 
    return {
      rpmType: "rpm",
      powerType: "kw",
      motor: new MotorData(),
      gearing: new GearingData()
    }
  },
  filters: {
    number: function (value: string, decimals: string) {
      if (!value) { return '' } 
      return parseFloat(value)
        .toFixed(parseInt(decimals))
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
  },
  computed: {
    shredderInputData: function (): ShredderInputData {
      return this.inputData as ShredderInputData
    },
    shredderRPM: function(): number | null {
      return this.getComputedShredderRPM()
    },
    shredderTorque: function(): number | null {
      return this.getComputedShredderTorque()
    },
    shredderSpeedRating: function(): RatingResult | null {
      return this.getShredderRPMRating();
    },
    shredderTorqueRating: function(): RatingResult | null {
      return this.getShredderTorqueRating();
    },
    shredderResultHint: function(): Hint | null {
      return this.getShredderHint();
    },
    shareResultsUrl: function() {
      let data = {
        rpm: this.getComputedMotorRPM(),
        kilowatts: (this.getComputedMotorWatts()! / 1000),
        gearRatio: this.gearing.ratio,
        maxTorque: this.gearing.maxTorque
      }
      let encodedData = encodeURIComponent(JSON.stringify(data))
      return `${window.location.origin}/?jsonInput=${encodedData}`
    },
  },
  methods: {
    rpmTypeChanged: function () {
      this.motor.rpm = null
      this.motor.hz = null
      this.motor.poles = null
    },

    powerTypeChanged: function () {
      this.motor.kilowatts = null
      this.motor.watts = null
      this.motor.volts = null
      this.motor.amps = null
    },

    getComputedMotorRPM: function(): number | null {
      if(this.motor.rpm) {
        return this.motor.rpm
      }
      else if(this.motor.hz && this.motor.poles) {
        return (this.motor.hz * 114) / this.motor.poles
      }
      return null
    },
  
    getComputedMotorWatts: function(): number | null {
      if (this.motor.watts) {
        return this.motor.watts
      }
      else if (this.motor.kilowatts) {
        return this.motor.kilowatts * 1000
      }
      else if (this.motor.volts && this.motor.amps) {
        return this.motor.volts * this.motor.amps * 0.8
      }
      return null
    },

    getComputedMotorTorque: function(): number | null {
      const computedRPM = this.getComputedMotorRPM()
      const computedWatts = this.getComputedMotorWatts()
      if(computedRPM && computedWatts) {
        return computedWatts / (computedRPM * 2 * Math.PI / 60)
      }
      return null
    },

    getComputedShredderTorque: function(): number | null {
      const computedMotorTorque = this.getComputedMotorTorque()
      if (this.gearing.ratio && computedMotorTorque) {
        return this.gearing.ratio * computedMotorTorque * this.gearing.efficiency
      } 
      return null
    },

    getComputedShredderRPM: function(): number | null {
      const computedRPM = this.getComputedMotorRPM()
      if (this.gearing.ratio && computedRPM) {
        return computedRPM / this.gearing.ratio
      } 
      return null
    },

    getShredderRPMRating: function(): RatingResult | null {
      let rpm = this.getComputedShredderRPM()
      if (!rpm) return null
      return getRating(this.shredderInputData.speedRatings, rpm)
    },

    getShredderTorqueRating: function(): RatingResult | null {
      let torque = this.getComputedShredderTorque()
      if (!torque) return null
      return getRating(this.shredderInputData.torqueRatings, torque)
    },

    getShredderHint: function(): Hint | null {
      if (!this.shredderTorqueRating || !this.shredderSpeedRating) {
        return null
      }

      let hintTitle: string = "No Data"
      let hintEmoji: string = ""
      let hints: string[] = []

      switch (this.shredderTorqueRating.value) {
        case 1:
          hints.push(`The torque is low.`)
          hintTitle = "Won't shred 😫"
          break
        case 2:
          hints.push(`Will shred light material but will jam often.`)
          hintTitle = "Poor 🤨"
          break
        case 3:
          hints.push(`Good torque, should not jam often. May jam occasionally on thicker plastic.`)
          hintTitle = "Good 😀"
          break
        case 4:
          hints.push(`Superior torque, should cope with thicker plastics and only jam infequently.`)
          hintTitle = "Superior 😆"
          break
        case 5:
          hintTitle = "Danger! 🚨"
          hints.push(`The torque is very high and may damage the shredder unit or gearbox.
                \n A less powerful motor would be better.`)
          if (this.shredderSpeedRating.value == 1 || this.shredderSpeedRating.value == 2) {
            hints.push(`This may be a good choice of motor but the gearbox has too high a reduction ratio.`)
          }
          break
      }

      switch (this.shredderSpeedRating.value) {
        case 1:
          hints.push(`The speed is too low`)
          if (this.shredderTorqueRating.value == 3 || this.shredderTorqueRating.value == 4) {
            hintTitle = "Marginal 😐"
          }
          break
        case 2:
          hints.push(`The speed is low.`)
          if (this.shredderTorqueRating.value == 3 || this.shredderTorqueRating.value == 4) {
            hintTitle = "Fair 🙂"
            hints.push(`This may not be a problem if you aren't in a hurry.`)
          }
          break
        case 3:
          hints.push(`Good speed.`)
          if (this.shredderTorqueRating.value == 2) {
            hintTitle = "Marginal 😐"
          }
          break
        case 4:
          hints.push(`The speed is high.`)
          if (this.shredderTorqueRating.value == 1 || this.shredderTorqueRating.value == 2) {
            hints.push(`You need to use a higher gear reduction.`)
          } else if (this.shredderTorqueRating.value == 3) {
            hintTitle = "Fair 🙂"
            hints.push(`It will shred but may have trouble feeding material due to the speed.`)
          } else if (this.shredderTorqueRating.value == 4) {
            hintTitle = "Fair 🙂"
            hints.push(`A larger gear reduction or slower motor may work better.`)
          }
          break
        case 5:
          hints.push(`The speed is very high.`)
           if (this.shredderTorqueRating.value == 1 || this.shredderTorqueRating.value == 2) {
            hints.push(`You need to use a higher gear reduction.`)
          }
          if (this.shredderTorqueRating.value == 3 || this.shredderTorqueRating.value == 4) {
            hintTitle = "Fair 🙂"
          }
          break
      }

      return new Hint(hintTitle, hints.join("\n"), hintEmoji)
    }
  }
})
